<template>
  <q-dialog
    v-model="modelValue"
    :maximized="isMaximized"
    :persistent="isPersistent"
    :full-width="isFullWidth"
    :position="dialogPosition"
    @hide="emit('hide')"
  >
    <q-card
      :class="'q-dialog-plugin dialog-container' + (isMaximized ? '-maximized': '') + ' ' + cssClass"
      :style="{ 'max-width': props.width, width: props.width, overflow: props.overflow}"
    >
      <div class="dialog-header">
        <slot v-if="!props.noHeader" name="header">
          <q-item :class="isAdmin || isTenantAdmin ? 'aptc-admin-dialogbar' : 'aptc-dialogbar'">
            <q-item-section>
              <q-item-label class="text-h6 text-weight-light text-white">
                {{ props.title }}
              </q-item-label>
              <q-item-label v-if="props.subTitle" class="text-white op-60" caption>
                {{ props.subTitle }}
              </q-item-label>
            </q-item-section>
            <q-item-section side>
              <slot name="actions" />
            </q-item-section>
            <q-item-section side>
              <q-btn
                rounded flat size="sm" dense
                icon="fa-light fa-xmark" color="white"
                @click.stop="onCancel"
              />
            </q-item-section>
          </q-item>
        </slot>
      </div>
      <q-btn
        v-if="!empty(slots.header) || props.noHeader"
        rounded flat size="sm" dense
        icon="fa-light fa-xmark" color="secondary"
        style="position: absolute; top: 18px; right: 18px; z-index:999"
        @click.stop="onCancel"
      />
      <div :class="'dialog-content' + (isMaximized ? '-maximized': '')" :style="{ overflow: props.overflow}">
        <slot name="default" />
      </div>
      <div>
        <slot name="footer" />
      </div>
    </q-card>
  </q-dialog>
</template>

<script setup>
  import { useDialogPluginComponent, useQuasar } from 'quasar'
  import { useVModel } from 'src/modules/core/composables/useVModel'
  import { computed, useSlots } from 'vue'
  import { isAdmin, isTenantAdmin } from 'src/services/session.js'
  import { empty } from 'src/utils/variable.js'

  const $q = useQuasar()
  const slots = useSlots()

  const props = defineProps(
    {
      modelValue: Boolean,
      title: String,
      subTitle: String,
      width: String,
      noHeader: Boolean,
      size: {
        validator(value) {
          return ['xs', 'sm', 'md', 'lg', 'xl'].includes(value)
        },
        default: 'md'
      },
      bgDark: Boolean,
      delegateClosure: Boolean,
      responsive: Boolean,
      persistent: Boolean,
      fullWidth: Boolean,
      position: String,
      maximized: Boolean,
      overflow: String
    }
  )

  const modelValue = useVModel(props, 'modelValue', false)

  const isMaximized = computed(() => props.maximized || $q.screen.lt.sm)
  const isPersistent = computed(() => props.persistent ?? (props.responsive ? !$q.screen.lt.sm : false))
  const isFullWidth = computed(() => props.fullWidth ?? (props.responsive ? $q.screen.lt.sm: false))
  const dialogPosition = computed(() => props.position ?? (props.responsive && $q.screen.lt.sm ? 'bottom' : 'standard'))

  const cssClass = computed(() => {
    let classes = props.size ? `dialog-${props.size}` : ''
    classes += props.bgDark ? ' bg-dark' : ''
    return classes.trim().length !== 0 ? classes : null
  })

  const emit = defineEmits([
    ...useDialogPluginComponent.emits,
    'update:modelValue',
    'cancel'
  ])

  function show() {
    modelValue.value = true
  }

  function hide() {
    modelValue.value = false
  }

  function onCancel() {
    if (!props.delegateClosure) {
      hide()
    }
    emit('cancel')
  }

  defineExpose({ show, hide })
</script>

<!--suppress CssUnusedSymbol They are used -->
<style scoped>
.dialog-container {
  display: flex;
  flex-direction: column;
  max-height: 90vh;
}

.dialog-container-maximized {
  display: flex;
  flex-direction: column;
  max-height: 100vh;
}

.dialog-header {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: inherit;
}

.dialog-content {
  flex: 1;
  overflow-y: auto;
  max-height: calc(90vh - 60px);
}

.dialog-content-maximized {
  flex: 1;
  overflow-y: auto;
  max-height: calc(100vh - 60px);
}
</style>
